// third party libraries
import {
  Alert,
  Checkbox,
  CheckboxButton,
  CheckboxGroup,
  Collapse,
  CollapseItem,
  Dialog,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Form,
  FormItem,
  Input,
  Loading,
  Menu,
  MenuItem,
  MenuItemGroup,
  Message,
  Option,
  OptionGroup,
  Popover,
  Progress,
  Radio,
  RadioButton,
  RadioGroup,
  Scrollbar,
  Select,
  Spinner,
  Step,
  Submenu,
  Switch,
  TabPane,
  Table,
  TableColumn,
  Tabs,
  Tooltip,
  Upload,
} from 'element-ui';

import Popper from 'vue-popperjs';
import Datepicker from 'vuejs-datepicker';
import VCalendar from 'v-calendar';
import vClickOutside from 'v-click-outside';

// custom components
import AppAlert from './components/AppAlert';
import AppButton from './components/AppButton';
import AppConfirmEmail from './components/AuthPages/AppConfirmEmail';
import AppForgotPasswordForm from './components/AuthPages/AppForgotPasswordForm';
import AppIcon from './components/AppIcon';
import AppLanding from './components/AuthPages/AppLanding';
import AppQuoteEditItem from './components/AppQuoteEditItem';
import AppRegisterConfirmation from './components/AuthPages/AppRegisterConfirmation';
import AppRegisterForm from './components/AuthPages/AppRegisterForm';
import AppResendConfirmation from './components/AuthPages/AppResendConfirmation';
import AppResetPasswordForm from './components/AuthPages/AppResetPasswordForm';
import AppSignIn from './components/AuthPages/AppSignIn';
import AppTermsOfUse from './components/AuthPages/AppTermsOfUse';
import AuthPageFooter from './components/AuthPages/Footer';
import AuthPageHeader from './components/AuthPages/Header';
import Comment from './components/Comment';
import CommentInput from './components/Comment/Input';
import CpCallout from './components/CpCallout';
import CopyLinkContainer from './components/CopyLinkContainer';
import RfpTable from './components/RfpTable';
import TfBadge from './components/TfBadge';
import TfButton from './components/TfButton';
import TfCheckbox from './components/TfForm/TfCheckbox';
import TfCtaEmailSupport from './components/TfCtaEmailSupport';
import TfCommentsExport from './components/TfPdfExport/Comments';
import TfDatepicker from './components/TfDatepicker';
import TfFilter from './components/TfFilter';
import TfFilterDateRangeInput from './components/TfFilter/DateRangeInput';
import TfFormBuilder from './components/TfForm/TfFormBuilder';
import TfFormList from './components/TfForm/TfFormList';
import TfHamburgerMenu from './components/HamburgerMenu';
import TfHamburgerMenuItem from './components/HamburgerMenu/MenuItem';
import TfInlineFormField from './components/TfForm/TfInlineFormField';
import TfInputWithOptions from './components/TfForm/TfInputWithOptions';
import TfMultiSelect from './components/TfForm/TfMultiSelect';
import TfPagination from './components/TfPagination';
import TfPanel from './components/TfPanel1';
import TfPanel2 from './components/TfPanel2';
import TfPasswordField from './components/TfForm/TfPasswordField';
import TfPdfExport from './components/TfPdfExport';
import TfPdfExportHeader from './components/TfPdfExport/Header';
import TfPopover from './components/TfPopover';
import TfProductSelector from './components/TfProductSelector';
import TfProgressIcon from './components/TfProductSelector/TfProgressIcon';
import TfQuoteEditsExport from './components/TfPdfExport/QuoteEdits';
import TfSelectText from './components/TfForm/TfSelectText';
import TfSubmenu from './components/TfSubmenu';
import TfTag from './components/TfForm/TfTag';
import TfTagField from './components/TfForm/TfTagField';
import TfTabs from './components/TfTabs';
import TfTable from './components/TfTable';
import TfTableChildRow from './components/TfTable/ChildRow';
import TfTableChildTable from './components/TfTable/ChildTable';
import TfTableParentRow from './components/TfTable/ParentRow';
import TfTableTd from './components/TfTable/Td';
import TfTooltip from './components/TfTooltip';
import ThreeflowWordmark from './components/ThreeflowWordmark';
import VueForceNextTick from './utils/vue-force-next-tick';

// utils
import Analytics from './utils/analytics';
import AxiosConfig from './utils/axios';
import BrowserUtils from './utils/browser';
import Cookies from './utils/cookies';
import DatesUtils from './utils/dates';
import Formatting from './utils/formatting';
import Product from './utils/product';
import ReusableData from './utils/reusable-data';
import Validation from './utils/validation';

// services
import CommentService from './services/comment';
import NotificationsService from './services/notifications';
import SourceService from './services/source';

/**
 * Every component that gets installed when someone calls Vue.use for our component
 *
 * @param {object} Vue
 */
const loadThreeflowComponents = function (Vue) {
  // Element components
  Vue.use(Alert);
  Vue.use(Checkbox);
  Vue.use(CheckboxButton);
  Vue.use(CheckboxGroup);
  Vue.use(Collapse);
  Vue.use(CollapseItem);
  Vue.use(Dialog);
  Vue.use(Dropdown);
  Vue.use(DropdownItem);
  Vue.use(DropdownMenu);
  Vue.use(Form);
  Vue.use(FormItem);
  Vue.use(Input);
  Vue.use(Loading.directive);
  Vue.use(Menu);
  Vue.use(MenuItem);
  Vue.use(MenuItemGroup);
  Vue.use(Option);
  Vue.use(OptionGroup);
  Vue.use(Popover);
  Vue.use(Progress);
  Vue.use(Radio);
  Vue.use(RadioButton);
  Vue.use(RadioGroup);
  Vue.use(Scrollbar);
  Vue.use(Select);
  Vue.use(Spinner);
  Vue.use(Step);
  Vue.use(Submenu);
  Vue.use(Switch);
  Vue.use(TabPane);
  Vue.use(Tabs);
  Vue.use(Table);
  Vue.use(TableColumn);
  Vue.use(Tooltip);
  Vue.use(Upload);

  // Third party libraries
  Vue.component('Popper', Popper);
  Vue.use(VueForceNextTick);

  // custom components
  Vue.component('AppAlert', AppAlert);
  Vue.component('AppButton', AppButton);
  Vue.component('AppConfirmEmail', AppConfirmEmail);
  Vue.component('AppForgotPasswordForm', AppForgotPasswordForm);
  Vue.component('AppIcon', AppIcon);
  Vue.component('AppLanding', AppLanding);
  Vue.component('AppRegisterConfirmation', AppRegisterConfirmation);
  Vue.component('AppRegisterForm', AppRegisterForm);
  Vue.component('AppResendConfirmation', AppResendConfirmation);
  Vue.component('AppResetPasswordForm', AppResetPasswordForm);
  Vue.component('AppSignIn', AppSignIn);
  Vue.component('AppTermsOfUse', AppTermsOfUse);
  Vue.component('AuthPageFooter', AuthPageFooter);
  Vue.component('AuthPageHeader', AuthPageHeader);
  Vue.component('AppQuoteEditItem', AppQuoteEditItem);
  Vue.component('Comment', Comment);
  Vue.component('CommentInput', CommentInput);
  Vue.component('CpCallout', CpCallout);
  Vue.component('CopyLinkContainer', CopyLinkContainer);
  Vue.component('Datepicker', Datepicker);
  Vue.component('RfpTable', RfpTable);
  Vue.component('TfBadge', TfBadge);
  Vue.component('TfButton', TfButton);
  Vue.component('TfCheckbox', TfCheckbox);
  Vue.component('TfCtaEmailSupport', TfCtaEmailSupport);
  Vue.component('TfCommentsExport', TfCommentsExport);
  Vue.component('TfDatepicker', TfDatepicker);
  Vue.component('TfFilter', TfFilter);
  Vue.component('TfFilterDateRangeInput', TfFilterDateRangeInput);
  Vue.component('TfFormBuilder', TfFormBuilder);
  Vue.component('TfFormList', TfFormList);
  Vue.component('TfHamburgerMenu', TfHamburgerMenu);
  Vue.component('TfHamburgerMenuItem', TfHamburgerMenuItem);
  Vue.component('TfInlineFormField', TfInlineFormField);
  Vue.component('TfInputWithOptions', TfInputWithOptions);
  Vue.component('TfMultiSelect', TfMultiSelect);
  Vue.component('TfPanel', TfPanel);
  Vue.component('TfPanel2', TfPanel2);
  Vue.component('TfPasswordField', TfPasswordField);
  Vue.component('TfPdfExport', TfPdfExport);
  Vue.component('TfPdfExportHeader', TfPdfExportHeader);
  Vue.component('TfPlanDesignDropdown', TfInputWithOptions);
  Vue.component('TfPopover', TfPopover);
  Vue.component('TfProductSelector', TfProductSelector);
  Vue.component('TfProgressIcon', TfProgressIcon);
  Vue.component('TfPagination', TfPagination);
  Vue.component('TfQuoteEditsExport', TfQuoteEditsExport);
  Vue.component('TfSelectText', TfSelectText);
  Vue.component('TfSubmenu', TfSubmenu);
  Vue.component('TfTable', TfTable);
  Vue.component('TfTableChildRow', TfTableChildRow);
  Vue.component('TfTableChildTable', TfTableChildTable);
  Vue.component('TfTableParentRow', TfTableParentRow);
  Vue.component('TfTableTd', TfTableTd);
  Vue.component('TfTabs', TfTabs);
  Vue.component('TfTag', TfTag);
  Vue.component('TfTagField', TfTagField);
  Vue.component('TfTooltip', TfTooltip);
  Vue.component('ThreeflowWordmark', ThreeflowWordmark);

  Vue.use(VCalendar);

  // element magic
  /* eslint-disable no-param-reassign */
  Vue.prototype.$loading = Loading.service;
  Vue.prototype.$message = Message;
  /* eslint-enable no-param-reassign */

  // plugins
  Vue.use(vClickOutside);
};

const threeflowObject = {
  Analytics,
  AxiosConfig,
  BrowserUtils,
  CommentService,
  Cookies,
  DatesUtils,
  Formatting,
  NotificationsService,
  Product,
  ReusableData,
  SourceService,
  Validation,
};

export { threeflowObject, loadThreeflowComponents };
